import React, { useState } from "react";
import { Menu, X, LayoutDashboard } from "lucide-react";
import FeaturesDropdown from "./FeaturesDropdown";
import { useTranslation } from "react-i18next";
import logoAsset from "../../assets/logo-asset.png";
import gsap from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { useNavigate } from "react-router-dom";

// Register ScrollToPlugin
gsap.registerPlugin(ScrollToPlugin);

interface NavbarProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean) => void;
  currentPage: string;
  setCurrentPage: (page: string) => void;
  onLogin?: () => void;
  onGetStarted?: () => void;
  onFeatureClick?: (tabIndex: number) => void;
  onNavFeatureClick?: () => void;
  onPricingClick?: () => void;
}

export default function Navbar({ isMenuOpen, setIsMenuOpen, currentPage, setCurrentPage, onLogin, onGetStarted, onFeatureClick, onNavFeatureClick, onPricingClick }: NavbarProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const handleTryDemo = () => {
    navigate('/demo/dashboard');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'id' : 'en';
    i18n.changeLanguage(newLang);
    localStorage.setItem('language', newLang);
  };

  const handlePricingClick = () => {
    if (currentPage !== 'home') {
      setCurrentPage('home');
      setTimeout(() => {
        const pricingSection = document.querySelector('[data-pricing-section]');
        if (pricingSection) {
          // Use GSAP scrollTo to bypass ScrollTrigger pinned sections
          gsap.to(window, {
            duration: 1.2,
            scrollTo: { 
              y: pricingSection,
              offsetY: 80, // Account for navbar height
              autoKill: true
            },
            ease: "power2.inOut"
          });
        }
      }, 300);
    } else {
      const pricingSection = document.querySelector('[data-pricing-section]');
      if (pricingSection) {
        // Use GSAP scrollTo to bypass ScrollTrigger pinned sections
        gsap.to(window, {
          duration: 1.2,
          scrollTo: { 
            y: pricingSection,
            offsetY: 80, // Account for navbar height
            autoKill: true
          },
          ease: "power2.inOut"
        });
      }
    }
    if (onPricingClick) onPricingClick();
  };

  return (
    <nav className="fixed top-0 inset-x-0 z-50 bg-[#050505]/95 backdrop-blur-md border-b border-white/5">
      <div className="max-w-[1200px] mx-auto px-6 h-20 flex items-center justify-between">
        <div className="flex items-center cursor-pointer" onClick={(e) => { e.preventDefault(); setCurrentPage('home'); setIsMenuOpen(false); }}>
          <img src={logoAsset} alt="Catat Crypto" className="h-14 w-auto object-contain" />
        </div>

        <div className="hidden md:flex items-center gap-8 h-full">
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); setCurrentPage('home'); }}
            className={`text-[13px] font-semibold h-full flex items-center transition-colors ${currentPage === 'home' ? 'text-[#E26A59] border-b-2 border-[#E26A59]' : 'text-neutral-400 hover:text-white border-b-2 border-transparent'}`}
          >
            {t('nav.home')}
          </a>
          <FeaturesDropdown setCurrentPage={setCurrentPage} onFeatureClick={onFeatureClick ?? (() => {})} />
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); handlePricingClick(); }}
            className={`text-[13px] font-semibold h-full flex items-center transition-colors text-neutral-400 hover:text-white border-b-2 border-transparent`}
          >
            {t('nav.pricing')}
          </a>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/partnership'); }}
            className={`text-[13px] font-semibold h-full flex items-center transition-colors text-neutral-400 hover:text-white border-b-2 border-transparent`}
          >
            {t('nav.partnership')}
          </a>
          <a
            href="https://docs.catatcrypto.com/getting-started/introduction/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[13px] font-semibold h-full flex items-center transition-colors text-neutral-400 hover:text-white border-b-2 border-transparent"
          >
            {t('nav.docs')}
          </a>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); setCurrentPage('contact'); }}
            className={`text-[13px] font-semibold h-full flex items-center transition-colors ${currentPage === 'contact' ? 'text-[#E26A59] border-b-2 border-[#E26A59]' : 'text-neutral-400 hover:text-white border-b-2 border-transparent'}`}
          >
            {t('nav.contact')}
          </a>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); setCurrentPage('about'); }}
            className={`text-[13px] font-semibold h-full flex items-center transition-colors ${currentPage === 'about' ? 'text-[#E26A59] border-b-2 border-[#E26A59]' : 'text-neutral-400 hover:text-white border-b-2 border-transparent'}`}
          >
            {t('nav.about')}
          </a>
        </div>

        <div className="hidden md:flex items-center gap-4">
          <button 
            onClick={toggleLanguage}
            className="relative flex items-center w-[56px] h-[30px] bg-[#121214] border border-white/10 rounded-full cursor-pointer p-[3px] transition-colors hover:border-white/20"
            aria-label="Toggle Language"
          >
            <div 
              className={`absolute top-[3px] left-[3px] w-[22px] h-[22px] bg-[#2E3136] rounded-full shadow-sm transition-transform duration-300 ease-in-out ${
                i18n.language === 'en' ? 'translate-x-0' : 'translate-x-[26px]'
              }`}
            />
            <div className="relative z-10 flex items-center justify-between w-full px-0.5 pointer-events-none">
              <span className={`text-[10px] font-bold w-[22px] text-center transition-colors ${i18n.language === 'en' ? 'text-white' : 'text-neutral-500'}`}>EN</span>
              <span className={`text-[10px] font-bold w-[22px] text-center transition-colors ${i18n.language === 'id' ? 'text-white' : 'text-neutral-500'}`}>ID</span>
            </div>
          </button>
          <button
            onClick={handleTryDemo}
            id="navbar-demo-btn"
            className="border-rotate-container"
          >
            <style>{`
              @keyframes borderRotate {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              .border-rotate-container {
                position: relative;
                overflow: hidden;
                padding: 1px;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                border-radius: 8px;
                background: transparent;
                border: none;
                box-shadow: 0 0 12px rgba(229,9,20,0.15);
              }
              .border-rotate-bg {
                position: absolute;
                top: -150%;
                left: -150%;
                width: 400%;
                height: 400%;
                background: conic-gradient(from 0deg, transparent 60%, #E50914 100%);
                animation: borderRotate 4s linear infinite;
                transform-origin: center;
              }
              .border-rotate-inner {
                position: relative;
                z-index: 10;
                display: flex;
                align-items: center;
                gap: 6px;
                padding: 8px 16px;
                border-radius: 7px;
                font-size: 13px;
                font-weight: 700;
                color: #ffffff;
                background: linear-gradient(135deg, #120707 0%, #1e0b0b 100%);
                width: 100%;
                height: 100%;
              }
              .border-rotate-inner-mobile {
                position: relative;
                z-index: 10;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                padding: 12px 20px;
                border-radius: 7px;
                font-size: 13px;
                font-weight: 700;
                color: #ffffff;
                background: linear-gradient(135deg, #120707 0%, #1e0b0b 100%);
                width: 100%;
                height: 100%;
              }
            `}</style>
            <div className="border-rotate-bg" />
            <div className="border-rotate-inner">
              <LayoutDashboard size={13} />
              Demo
            </div>
          </button>
          <button 
            onClick={onLogin}
            className="text-[13px] font-semibold text-white px-5 py-2 hover:bg-white/5 rounded-full border border-white/10 transition-colors"
          >
            {t('nav.login')}
          </button>
          <button 
            onClick={onGetStarted}
            className="px-5 py-2 bg-[#CC1010] hover:bg-[#E22222] text-white text-[13px] font-bold rounded-lg transition-all shadow-lg"
          >
            {t('nav.getStarted')}
          </button>
        </div>

        <button
          className="md:hidden text-neutral-400 hover:text-white transition-colors"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden absolute top-20 left-0 w-full bg-[#050505] border-b border-white/5 flex flex-col items-center py-6 gap-6 shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200">
           <button 
              onClick={toggleLanguage}
              className="relative flex items-center w-[120px] h-[36px] bg-[#121214] border border-white/10 rounded-full cursor-pointer p-[4px] transition-colors hover:border-white/20"
              aria-label="Toggle Language"
            >
              <div 
                className={`absolute top-[4px] left-[4px] w-[54px] h-[26px] bg-[#2E3136] rounded-full shadow-sm transition-transform duration-300 ease-in-out ${
                  i18n.language === 'en' ? 'translate-x-0' : 'translate-x-[56px]'
                }`}
              />
              <div className="relative z-10 flex items-center justify-between w-full px-2 pointer-events-none">
                <span className={`text-[11px] font-bold w-[50px] text-center transition-colors ${i18n.language === 'en' ? 'text-white' : 'text-neutral-500'}`}>EN</span>
                <span className={`text-[11px] font-bold w-[50px] text-center transition-colors ${i18n.language === 'id' ? 'text-white' : 'text-neutral-500'}`}>ID</span>
              </div>
          </button>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); setCurrentPage('home'); setIsMenuOpen(false); }}
            className={`text-sm font-semibold transition-colors ${currentPage === 'home' ? 'text-[#E26A59]' : 'text-neutral-400 hover:text-white'}`}
          >
            {t('nav.home')}
          </a>
           <a
            href="#"
            onClick={(e) => { e.preventDefault(); onNavFeatureClick?.(); setIsMenuOpen(false); }}
            className={`text-sm font-semibold transition-colors text-neutral-400 hover:text-white`}
          >
            {t('nav.features')}
          </a>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); handlePricingClick(); setIsMenuOpen(false); }}
            className={`text-sm font-semibold transition-colors text-neutral-400 hover:text-white`}
          >
            {t('nav.pricing')}
          </a>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/partnership'); setIsMenuOpen(false); }}
            className={`text-sm font-semibold transition-colors text-neutral-400 hover:text-white`}
          >
            {t('nav.partnership')}
          </a>
          <a
            href="https://docs.catatcrypto.com/getting-started/introduction/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold transition-colors text-neutral-400 hover:text-white"
          >
            {t('nav.docs')}
          </a>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); setCurrentPage('contact'); setIsMenuOpen(false); }}
            className={`text-sm font-semibold transition-colors ${currentPage === 'contact' ? 'text-[#E26A59]' : 'text-neutral-400 hover:text-white'}`}
          >
            {t('nav.contact')}
          </a>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); setCurrentPage('about'); setIsMenuOpen(false); }}
            className={`text-sm font-semibold transition-colors ${currentPage === 'about' ? 'text-[#E26A59]' : 'text-neutral-400 hover:text-white'}`}
          >
            {t('nav.about')}
          </a>
          <div className="flex flex-col items-center gap-3 mt-2 w-full px-6">
            <button
              onClick={handleTryDemo}
              id="navbar-demo-btn-mobile"
              className="border-rotate-container w-full"
            >
              <div className="border-rotate-bg" />
              <div className="border-rotate-inner-mobile">
                <LayoutDashboard size={14} />
                Demo
              </div>
            </button>
            <button 
              onClick={onLogin}
              className="w-full text-[13px] font-semibold text-white px-5 py-3 rounded-full border border-white/10 transition-colors"
            >
              {t('nav.login')}
            </button>
            <button 
              onClick={onGetStarted}
              className="w-full px-5 py-3 bg-[#CC1010] hover:bg-[#E22222] text-white text-[13px] font-bold rounded-lg transition-all shadow-lg"
            >
              {t('nav.getStarted')}
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
